import { Panel } from "../panel/Panel";
import { getLogger, getMapSettings } from "@mcleod/core";
import { VendorMap, Map } from "./Map";
import { MapVendor, VendorMapProps } from "./MapProps";
import { MapPin } from "./MapPin";
import TrimbleMaps, { Marker } from "@trimblemaps/trimblemaps-js";
import { Head } from '@mcleod/core';
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { getSVGUrl } from "@mcleod/images";
import { Anchor } from "../../page/Anchor";

const log = getLogger("components/Map");

export class TrimbleMap extends Panel implements VendorMap {
  map: TrimbleMaps.Map;
  parentMap: Map;
  route: TrimbleMaps.Route;
  markers: TrimbleMaps.Marker[];

  constructor(props?: Partial<VendorMapProps>) {
    super({ borderRadius: 4, ...props });
    if (props.parentMap == null)
      throw new Error("TrimbleMap needs to have its parentMap property set.");
    this.markers = [];
    this.parentMap.vendor = MapVendor.TRIMBLE;
  }

  async init() {
    try {
      TrimbleMaps.APIKey = getMapSettings().trimble_settings.api_key1 ? getMapSettings().trimble_settings.api_key1 : getMapSettings().trimble_settings.api_key2;
    }
    catch (error) {
      log.info(error);
    }
    if (TrimbleMaps.APIKey) {
      await this.initHeader();
    }
    else {
      ReflectiveDialogs.showSnackbar("No API key found for Trimble. Map cannot be shown");
    }
  }

  private initHeader(): Promise<void> {
    const cssUrl = "https://maps-sdk.trimblemaps.com/v3/trimblemaps-3.7.0.css";
    Head.removeLink(cssUrl);
    const link = Head.addLink(cssUrl);

    const jsUrl = "https://maps-sdk.trimblemaps.com/v3/trimblemaps-3.7.0.js";
    Head.removeScript(jsUrl);
    const script = Head.addScript(jsUrl);

    return new Promise((resolve, reject) => {
      link.onload = () => {
        log.debug("Trimble CSS was initialized successfully");
        resolve();
      };
      script.onload = () => {
        this.initMap();
        log.debug("Map initialized successfully");
        resolve();
      };
      link.onerror = () => {
        log.debug("An error occurred during CSS initialization");
        reject();
      };
      script.onerror = () => {
        log.debug("An error occurred during map initialization");
        reject();
      };
    });
  }

  private initMap() {
    this.map = new TrimbleMaps.Map({
      container: this._element,
      center: new TrimbleMaps.LngLat(-96, 35),
      //style: "satellite",
      zoom: 3
    });

    this.map.addControl(new TrimbleMaps.NavigationControl());
    const scale = new TrimbleMaps.ScaleControl({
      maxWidth: 80,
      unit: 'imperial'
    });
    this.map.addControl(scale);
    this.map.resize();
  }

  createRoute(routeData: any[]) {
    const data = this.formatTrimbleRouteData(routeData);
    if (this.route) {
      this.route.remove();
    }
    this.route = new TrimbleMaps.Route({
      routeId: "myRoute",
      stops: data
    });
    this.route.addTo(this.map);
  }

  clearRoute() {
    if (this.route) {
      this.route.remove();
    }
  }

  private formatTrimbleRouteData(routeData: any[]): TrimbleMaps.LngLat[] {
    const coordinatesList = [];
    routeData.forEach(stop => {
      const lat = Number(stop.latitude);
      const lng = Number(Math.abs(stop.longitude) * -1);
      coordinatesList.push(new TrimbleMaps.LngLat(lng, lat));
    });
    return coordinatesList;
  }

  removeAllPins() {
    if(this.markers != null)
    for (const marker of this.markers)
      marker.remove();
    this.markers = [];
  }

  addPin(pin: MapPin) {
    if (this.map == null)
      return;

      const el = document.createElement('div');
      el.classList.add('cust-marker');
      el.title = pin.tooltip;

      if(pin.caption == null) {
        pin.caption = "";
      }

      const length = pin.caption.length;
      if(length == 1)
        pin.caption = " " + pin.caption + "   ";
      if(length == 2)
        pin.caption = pin.caption + "  ";

      const htmlContent =
      '<div id="image" style="background: url('+ getSVGUrl(pin.image) + ') no-repeat; height: 36px; width: 36px;"/>' +
      '<div><pre vertical-align="bottom" align="center"  style="color:' + pin.captionColor + '">' +  pin.caption  + '</pre></div>';

      el.innerHTML = htmlContent;

      const marker = new Marker({ anchor: "bottom", element: el });
      marker.setLngLat({ lat: pin.latitude, lon: pin.longitude });
      marker.addTo(this.map);

      this.markers.push(marker);
      if (this.parentMap.pinNeedsClickListener(pin))
        el.addEventListener('click', (event) => this.parentMap.pinClicked(pin, event));

  }

  fitPins() {
    if (this.map == null)
      return;
    this.map.resize();

    this.setCenter();
    this.zoomAll();
  }

  zoomAll() {
    if(this.markers.length == 1) {
      this.setZoom(10);
    }
    else if (this.markers.length > 1) {
      const latLngMax = { lng: (-1) * this.getLngMax(), lat: this.getLatMax() };
      const latLngMin = { lng: (-1) * this.getLngMin(), lat: this.getLatMin() };
      const bounds = this.map.getBounds();
      bounds.setNorthEast(latLngMin);
      bounds.setSouthWest(latLngMax);
      this.map.fitBounds(bounds, { padding: 50 });

      if(this.map.getZoom() > 10)
        this.setZoom(10);
    }
  }

  setZoom(level: number) {
    if(this.map != null)
      this.map.zoomTo(level);
  }

  setCenter() {
    if(this.markers.length == 0)
      return;

    const latitude = (this.getLatMax() + this.getLatMin())/2;
    const longitude = (-1)*(this.getLngMax() + this.getLngMin())/2;
    this.map.setCenter(new TrimbleMaps.LngLat(longitude, latitude));
  }

  getLatMax() {
    let latMax = 0;
    for (const marker of this.markers) {
      if (latMax < marker.getLngLat().lat)
        latMax = marker.getLngLat().lat;
    }
    return (latMax);
  }
  getLatMin() {
    let latMin = 500;
    for (const marker of this.markers) {
      if (latMin > marker.getLngLat().lat)
        latMin = marker.getLngLat().lat;
    }
    return (latMin);
  }
  getLngMax() {
    let lngMax = 0;
    for (const marker of this.markers) {
      if (lngMax < (-1) * marker.getLngLat().lng)
        lngMax = (-1) * marker.getLngLat().lng;
    }
    return (lngMax);
  }
  getLngMin() {
    let lngMin = 500;
    for (const marker of this.markers) {
      if (lngMin > (-1) * marker.getLngLat().lng)
        lngMin = (-1) * marker.getLngLat().lng;
    }
    return (lngMin);
  }
}
